import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useSiteSettings } from '../contexts/SiteSettingsContext';
import { useI18n } from '../i18n/I18nContext';
import { API_ENDPOINTS } from '../config/api';
import { couponAmountLabel, findMainCoupon, isCouponLive, parseCouponDate } from '../lib/booking/pricing';
import { CATEGORIES_SETTING_KEY, parseCategories } from '../lib/booking/categories';

// ── Site-wide promo bar ──────────────────────────────────────────────────────
// Thin, site-wide banner for whatever offer the owner has made the main one.
//
// Nothing about the offer is written here any more. The code comes from
// site_settings.main_coupon_code (set in Admin → Coupons), and the percentage,
// the end date and whether it is still live come from that coupon's own row in
// GET /api/coupons-api. The bar used to hardcode SUMMER20 and then SEPTEMBER20
// with their own end dates, which is how the banner, the calendar and the
// checkout ended up quoting three different offers. It now renders the offer
// this visit last resolved to while the rows are in flight (see below), and
// nothing at all when there is no live one.
//
// This strip is in normal flow INSIDE the fixed Header. The body class only
// supplies the pre-hydration height estimate; Header's ResizeObserver measures
// the complete visible block after mount. No independent fixed promo layer or
// matching navigation top offset can drift apart on scroll.
//
// First paint uses the prerendered offer or the build's public snapshot;
// localStorage covers development/older builds. Each is checked for dates and
// usage limits before rendering. The live API replaces it once available.
// A new or changed offer since the build can still change the strip on refresh;
// ordinary first visits no longer remove/reinsert an already-known offer.

const CACHE_KEY = 'promoBarCoupon';

const readCachedCoupon = () => {
  // Retain the offer in the HTML on a first visit, before hydration replaces
  // the tree. localStorage is empty then; waiting for the API collapses and
  // reopens the 44px strip. Only public coupon fields are embedded below.
  try {
    const raw = document.querySelector('[data-promo-coupon]')?.getAttribute('data-promo-coupon');
    const row = raw ? JSON.parse(raw) : null;
    if (isCouponLive(row)) return row;
  } catch (_) { /* no valid prerendered offer */ }
  try {
    const encoded = process.env.REACT_APP_PROMO_SNAPSHOT;
    const row = encoded ? JSON.parse(atob(encoded)) : null;
    if (isCouponLive(row)) return row;
  } catch (_) { /* development builds may have no snapshot */ }
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    const row = raw ? JSON.parse(raw) : null;
    return isCouponLive(row) ? row : null;
  } catch (e) {
    return null;
  }
};

// The bar translates itself (data-i18n-react keeps the site's DOM translator
// out): machine translation turned "September offer" into "September
// sacrifice" in five languages and the code SEPTEMBER20 into a date.
// Month names, dates and percentages come from Intl.
const PROMO_COPY = {
  en: { title: (m) => `${m} offer`, current: 'Current offer', code: 'code', through: (d) => `through ${d}`,
    deal: (a, tours) => `${a} off${tours ? ' Central Park carriage tours' : ''}` },
  es: { title: (m) => `Oferta de ${m}`, current: 'Oferta actual', code: 'código', through: (d) => `hasta el ${d}`,
    deal: (a, tours) => `${a} de descuento${tours ? ' en paseos en carruaje por Central Park' : ''}` },
  fr: { title: (m) => (/^[aeiouh]/i.test(m) ? `Offre d’${m}` : `Offre de ${m}`), current: 'Offre du moment', code: 'code',
    through: (d) => `jusqu’au ${d}`, deal: (a, tours) => `${a} de réduction${tours ? ' sur les promenades en calèche à Central Park' : ''}` },
  de: { title: (m) => `Angebot im ${m}`, current: 'Aktuelles Angebot', code: 'Code', through: (d) => `bis ${d}`,
    deal: (a, tours) => `${a} Rabatt${tours ? ' auf Kutschfahrten im Central Park' : ''}` },
  it: { title: (m) => `Offerta di ${m}`, current: 'Offerta attuale', code: 'codice', through: (d) => `fino al ${d}`,
    deal: (a, tours) => `${a} di sconto${tours ? ' sui giri in carrozza a Central Park' : ''}` },
  pt: { title: (m) => `Oferta de ${m}`, current: 'Oferta atual', code: 'código', through: (d) => `até ${d}`,
    deal: (a, tours) => `${a} de desconto${tours ? ' em passeios de carruagem no Central Park' : ''}` },
  nl: { title: (m) => `Aanbieding van ${m}`, current: 'Huidige aanbieding', code: 'code', through: (d) => `t/m ${d}`,
    deal: (a, tours) => `${a} korting${tours ? ' op koetsritten door Central Park' : ''}` },
  ru: { title: (m) => `Предложение на ${m}`, current: 'Специальное предложение', code: 'код', through: (d) => `до ${d}`,
    deal: (a, tours) => `скидка ${a}${tours ? ' на прогулки в карете по Центральному парку' : ''}` },
  tr: { title: (m) => `${m} fırsatı`, current: 'Güncel fırsat', code: 'kod', through: (d) => `${d} tarihine kadar`,
    deal: (a, tours) => `${tours ? 'Central Park fayton turlarında ' : ''}${a} indirim` },
  ar: { title: (m) => `عرض ${m}`, current: 'العرض الحالي', code: 'الرمز', through: (d) => `حتى ${d}`,
    deal: (a, tours) => `خصم ${a}${tours ? ' على جولات العربات في سنترال بارك' : ''}` },
  zh: { title: (m) => `${m}优惠`, current: '当前优惠', code: '优惠码', through: (d) => `截至${d}`,
    deal: (a, tours) => `${tours ? '中央公园马车游' : ''}优惠${a}` },
  ja: { title: (m) => `${m}の特別オファー`, current: '現在のオファー', code: 'コード', through: (d) => `${d}まで`,
    deal: (a, tours) => `${tours ? 'セントラルパーク馬車ツアーが' : ''}${a}オフ` },
  ko: { title: (m) => `${m} 특별 할인`, current: '진행 중인 할인', code: '코드', through: (d) => `${d}까지`,
    deal: (a, tours) => `${tours ? '센트럴 파크 마차 투어 ' : ''}${a} 할인` },
  hi: { title: (m) => `${m} ऑफ़र`, current: 'मौजूदा ऑफ़र', code: 'कोड', through: (d) => `${d} तक`,
    deal: (a, tours) => `${tours ? 'सेंट्रल पार्क बग्गी टूर पर ' : ''}${a} की छूट` },
};
// Western digits in Arabic, so the date and percentage match the code beside them.
const INTL_LOCALE = { en: 'en-US', ar: 'ar-u-nu-latn' };

/** "20%" / "%20" / "20 %" per locale; a fixed discount stays "$25". */
function amountFor(coupon, locale) {
  if (coupon.discount_type !== 'percentage') return couponAmountLabel(coupon).replace(/ off$/, '');
  return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 2 })
    .format((parseFloat(coupon.discount_value) || 0) / 100);
}

const PromoBar = () => {
  const { lang } = useI18n();
  const { settings, loading: settingsLoading } = useSiteSettings();
  const mainCode = settings?.main_coupon_code;

  // What the previous visit resolved to; stands in until this one has both
  // halves of the answer (the coupon rows AND site_settings, which carries the
  // code). Read once, synchronously, so the first render already knows.
  const [cachedCoupon] = useState(readCachedCoupon);

  // Revalidate the public snapshot. A network failure retains only a coupon
  // that already passed the date/use-limit checks, never an invented discount.
  const [coupons, setCoupons] = useState(null);
  useEffect(() => {
    const controller = new AbortController();
    fetch(API_ENDPOINTS.coupons, { signal: controller.signal })
      .then((r) => { if (!r.ok) throw new Error('Coupon refresh unavailable'); return r.json(); })
      .then((rows) => {
        const list = Array.isArray(rows) ? rows : rows?.coupons || [];
        setCoupons(list.filter((c) => c && c.code));
      })
      .catch(() => {});
    return () => controller.abort();
  }, []);

  const settled = coupons !== null && !settingsLoading;
  const coupon = useMemo(
    () => (settled ? findMainCoupon(coupons, mainCode) : cachedCoupon),
    [settled, coupons, mainCode, cachedCoupon]
  );

  // Link straight into the sale category on /book when one carries this code,
  // so the discount is already applied when the guest arrives.
  const href = useMemo(() => {
    if (!coupon) return '/book';
    const code = String(coupon.code).toUpperCase();
    const category = parseCategories(settings?.[CATEGORIES_SETTING_KEY]).find(
      (c) => c.couponCode === code
    );
    return category ? `/book/${category.id}` : '/book';
  }, [coupon, settings]);

  const show = Boolean(coupon);

  // A layout effect, not an effect: it has to run in the same frame as the
  // render that puts the bar on screen, or the header spends one paint at
  // top: 0 underneath it — which is the shift this whole arrangement exists to
  // avoid.
  useLayoutEffect(() => {
    if (!show) {
      // A page prerendered while an offer was live carries body.promo-active in
      // its static HTML; once this visit has resolved to "no live offer" the
      // header offset must go too, or every page keeps an empty strip on top.
      document.body.classList.remove('promo-active');
      return undefined;
    }
    document.body.classList.add('promo-active');
    return () => document.body.classList.remove('promo-active');
  }, [show]);

  // Written after paint, never during it. `null` is stored too: "no offer" is
  // exactly the answer that lets the next load reserve nothing.
  useEffect(() => {
    if (!settled) return;
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(coupon));
    } catch (e) { /* private mode / quota — the bar just falls back to fetching */ }
  }, [settled, coupon]);

  if (!show) return null;

  const copy = PROMO_COPY[lang] || PROMO_COPY.en;
  const locale = INTL_LOCALE[lang] || lang || 'en-US';
  const code = String(coupon.code).toUpperCase();
  const amount = amountFor(coupon, locale);
  const until = parseCouponDate(coupon.valid_until);
  const month = until ? until.toLocaleDateString(locale, { month: 'long' }) : null;
  const through = until ? until.toLocaleDateString(locale, { month: 'short', day: 'numeric' }) : null;
  const title = month ? copy.title(month) : copy.current;

  return (
    <aside aria-label="Current carriage ride offer">
    <a
      href={href}
      data-promo-coupon={JSON.stringify({
        code: coupon.code, discount_type: coupon.discount_type,
        discount_value: coupon.discount_value, is_active: coupon.is_active,
        valid_from: coupon.valid_from, valid_until: coupon.valid_until,
        booking_date_until: coupon.booking_date_until,
        max_uses: coupon.max_uses, used_count: coupon.used_count,
      })}
      className="promo-bar flex items-center justify-center gap-x-2 bg-brand-800 text-white text-center px-4 no-underline hover:bg-brand-900 transition-colors"
      data-i18n-react=""
    >
      <span className="text-brand-300" aria-hidden="true">☀</span>
      <span className="font-body text-xs sm:text-sm tracking-wide leading-none">
        <span className="font-semibold">{title}</span>
        <span className="hidden sm:inline"> · {copy.deal(amount, true)} · {copy.code} </span>
        <span className="sm:hidden"> · {copy.deal(amount, false)} · {copy.code} </span>
        <span className="font-bold text-brand-300" translate="no" dir="ltr">{code}</span>
        {through && <span className="hidden sm:inline"> · {copy.through(through)}</span>}
      </span>
    </a>
    </aside>
  );
};

export default PromoBar;
